// Apolo IDE — ThemeProvider (Sprint v10 light mode).
//
// Owns the theme state and synchronizes <html> class + <meta theme-color>.
// Three states:
//   - 'light'  : user explicitly chose light
//   - 'dark'   : user explicitly chose dark
//   - 'system' : follow OS prefers-color-scheme (default for new users)
//
// FOUC prevention: a small inline script in index.html sets the html class
// BEFORE this provider mounts, using the same localStorage key + matchMedia
// lookup. This component is the authoritative source after mount.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { THEME_STORAGE_KEY, THEMES, ThemeContext } from '../hooks/useTheme.js';
import { readAccent, writeAccent, applyAccent, DEFAULT_ACCENT } from '../lib/accentTheme.js';

const VALID_PREFERENCES = new Set([THEMES.LIGHT, THEMES.DARK, THEMES.SYSTEM]);

const META_COLOR = Object.freeze({
  dark: '#0a0a0a',
  light: '#ffffff',
});

function readStoredPreference() {
  if (typeof window === 'undefined') return THEMES.SYSTEM;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw && VALID_PREFERENCES.has(raw)) return raw;
  } catch {
    // localStorage may be blocked (privacy mode, Electron sandbox); fall through.
  }
  return THEMES.SYSTEM;
}

function readSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return THEMES.DARK;
  return window.matchMedia('(prefers-color-scheme: light)').matches
    ? THEMES.LIGHT
    : THEMES.DARK;
}

function resolvePreference(preference, systemTheme) {
  return preference === THEMES.SYSTEM ? systemTheme : preference;
}

function applyToDom(resolved) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('light', resolved === THEMES.LIGHT);
  root.classList.toggle('dark', resolved === THEMES.DARK);

  // Keep the dynamic theme-color meta in sync so the browser/PWA chrome
  // (status bar in iOS standalone, Chrome address bar) matches the theme.
  let meta = document.querySelector('meta[name="theme-color"]:not([media])');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', META_COLOR[resolved] ?? META_COLOR.dark);
}

export default function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(readStoredPreference);
  const [systemTheme, setSystemTheme] = useState(readSystemTheme);
  // design-uplift E2 — accent tint (hue + intensity). Default reproduces the
  // tokens.css blue exactly (applyAccent clears its vars at the default).
  const [accent, setAccentState] = useState(readAccent);

  const resolved = resolvePreference(preference, systemTheme);

  // Apply to DOM on every change (incl. first mount).
  useEffect(() => {
    applyToDom(resolved);
  }, [resolved]);

  // Re-apply the accent retint whenever the accent OR the resolved theme
  // changes (the per-theme base lightness/saturation differ). Inline vars on
  // <html> win over :root, so the retint cascades to all token consumers.
  useEffect(() => {
    applyAccent(accent, resolved);
  }, [accent, resolved]);

  // Listen for OS-level color scheme changes (only matters when preference = system).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (event) => {
      setSystemTheme(event.matches ? THEMES.LIGHT : THEMES.DARK);
    };
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14 fallback.
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  const setTheme = useCallback((next) => {
    if (!VALID_PREFERENCES.has(next)) return;
    setPreference(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // ignore — preference still lives in memory for the session.
    }
  }, []);

  const toggle = useCallback(() => {
    // Toggle flips between explicit dark ↔ light. If user is on 'system',
    // toggle commits to the opposite of what they're seeing right now.
    setTheme(resolved === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT);
  }, [resolved, setTheme]);

  const setAccent = useCallback((next) => {
    setAccentState(writeAccent(next));
  }, []);

  const resetAccent = useCallback(() => {
    setAccentState(writeAccent(DEFAULT_ACCENT));
  }, []);

  const value = useMemo(
    () => ({
      theme: preference,
      resolved,
      setTheme,
      toggle,
      accent,
      setAccent,
      resetAccent,
    }),
    [preference, resolved, setTheme, toggle, accent, setAccent, resetAccent]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
